import { lazy, ReactNode, Suspense, ComponentType } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import { route_path } from '@app/utils/route_path';
import { WithoutHeaderLayout } from '@app/components/layout';
import { LoginPage, MainPage, RedirectPaymentPage } from '@app/pages';
import { RootLayout, DefaultLayout, StageLayout, CommonOutlet } from '@app/app/layouts';
import { LoadingScreen } from '@app/components/template';
import ErrorBoundary from '@app/common/errorBoundary';
import { retryFetch } from '@app/app/lib/api';

import { logChunkLoadError } from './chunkLogger';
import { LazyLoadErrorFallback } from './LazyErrorFallback';

// ErrorBoundary Layout для роутера
const ErrorBoundaryLayout = () => (
  <ErrorBoundary>
    <Outlet />
  </ErrorBoundary>
);

// Wrapper для lazy компонентов с retry механизмом и логированием только ошибок
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithErrorBoundary = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  componentName?: string,
): React.LazyExoticComponent<T> => {
  return lazy(() => {
    const startTime = performance.now();
    const loadId = `${componentName || 'unknown'}_${Date.now()}`;
    const maxAttempts = 3;

    return retryFetch({
      cb: importFunc,
      retries: 2, // 3 попытки = 2 retry + 1 изначальная
      delayBetween: 500, // Увеличиваем задержку для нестабильного интернета
    }).catch((error) => {
      const loadTime = performance.now() - startTime;

      // Логируем только ошибки с деталями по попыткам
      logChunkLoadError({
        error,
        componentName: componentName || 'unknown',
        loadId,
        loadTime,
        attempt: maxAttempts,
        maxAttempts,
      });

      return {
        default: (() => (
          <LazyLoadErrorFallback
            error={error instanceof Error ? error : new Error('Lazy loading error')}
          />
        )) as unknown as T,
      };
    });
  });
};

const FinalizeStagePage = lazyWithErrorBoundary(
  () => import('@app/pages/finalize-stage'),
  'FinalizeStagePage',
);
const SharingPage = lazyWithErrorBoundary(() => import('@app/pages/sharing'), 'SharingPage');
const TextToSlidesPage = lazyWithErrorBoundary(
  () => import('@app/pages/text-to-slides'),
  'TextToSlidesPage',
);
const TextToSlidesPreviewPage = lazyWithErrorBoundary(
  () => import('@app/pages/text-to-slides-preview'),
  'TextToSlidesPreviewPage',
);
const RegistrationPage = lazyWithErrorBoundary(
  () => import('@app/pages/registration'),
  'RegistrationPage',
);
const CreateStartPage = lazyWithErrorBoundary(() => import('@app/pages/create'), 'CreateStartPage');
const LegalPage = lazyWithErrorBoundary(() => import('@app/pages/legal/legal.page'), 'LegalPage');
const TermsPage = lazyWithErrorBoundary(() => import('@app/pages/legal/terms.page'), 'TermsPage');
const PrivacyPage = lazyWithErrorBoundary(
  () => import('@app/pages/legal/privacy.page'),
  'PrivacyPage',
);
const CookiePage = lazyWithErrorBoundary(
  () => import('@app/pages/legal/cookie.page'),
  'CookiePage',
);
const FeaturesPage = lazyWithErrorBoundary(
  () => import('@app/pages/features/features.page'),
  'FeaturesPage',
);
const AboutPage = lazyWithErrorBoundary(() => import('@app/pages/about/about.page'), 'AboutPage');
const Wondercheck = lazyWithErrorBoundary(
  () => import('@app/wondercheck/wondercheck.js'),
  'Wondercheck',
);
const PricingPage = lazyWithErrorBoundary(
  () => import('@app/pages/pricing/pricing.page'),
  'PricingPage',
);
const PasswordReset = lazyWithErrorBoundary(
  () => import('@app/auth/passwordReset.js'),
  'PasswordReset',
);
const PasswordResetConfirm = lazyWithErrorBoundary(
  () => import('@app/auth/passwordResetConfirm.js'),
  'PasswordResetConfirm',
);
const Error404Page = lazyWithErrorBoundary(
  () => import('@app/pages/error/error404.page'),
  'Error404Page',
);
const CanvasDemoPage = lazyWithErrorBoundary(
  () => import('@app/pages/canvas-demo/canvas-demo.page'),
  'CanvasDemoPage',
);
const ProjectIdPage = lazyWithErrorBoundary(
  () => import('@app/pages/project/project-id.page'),
  'ProjectIdPage',
);
const ProjectsPage = lazyWithErrorBoundary(
  () => import('@app/pages/projects/projects.page'),
  'ProjectsPage',
);
const SetupStylePage = lazyWithErrorBoundary(
  () => import('@app/pages/create/setup-style.page'),
  'SetupStylePage',
);
const UploadDraftPage = lazyWithErrorBoundary(
  () => import('@app/pages/create/upload-draft.page'),
  'UploadDraftPage',
);
const UploadDraftPageNew = lazyWithErrorBoundary(
  () => import('@app/pages/upload-draft'),
  'UploadDraftPageNew',
);

const loadWithSuspense = (component: ReactNode) => (
  <Suspense fallback={<LoadingScreen />}>{component}</Suspense>
);

export const router = () =>
  createBrowserRouter([
    {
      element: <ErrorBoundaryLayout />,
      children: [
        {
          element: <RootLayout />,
          children: [
            {
              path: route_path.main,
              element: loadWithSuspense(<CommonOutlet />),
              //TODO: Refactor! Must be in default layout
              children: [
                { index: true, element: loadWithSuspense(<MainPage />) },
                { path: route_path.login, element: loadWithSuspense(<LoginPage />) },
                { path: route_path.register, element: loadWithSuspense(<RegistrationPage />) },
                { path: route_path.passwordReset, element: loadWithSuspense(<PasswordReset />) },
                {
                  path: `${route_path.passwordReset}:token`,
                  element: loadWithSuspense(<PasswordResetConfirm />),
                },
                //TODO: Refactor! Must be in default layout
                {
                  path: route_path.main,
                  element: <WithoutHeaderLayout />,
                  children: [
                    { path: route_path.about, element: loadWithSuspense(<AboutPage />) },
                    { path: route_path.features, element: loadWithSuspense(<FeaturesPage />) },
                  ],
                },

                /***** Default pages *****/
                {
                  path: route_path.main,
                  element: <DefaultLayout />,
                  children: [
                    { path: route_path.pricing, element: loadWithSuspense(<PricingPage />) },
                    { path: route_path.create, element: loadWithSuspense(<CreateStartPage />) },
                    {
                      path: route_path.workspaceCreate,
                      element: loadWithSuspense(<CreateStartPage isWorkspaceVariant />),
                    },
                    { path: route_path.projects, element: loadWithSuspense(<ProjectsPage />) },
                    { path: '/canvas-demo', element: loadWithSuspense(<CanvasDemoPage />) },
                    {
                      path: `${route_path.redirectPayment}/*`,
                      element: loadWithSuspense(<RedirectPaymentPage />),
                    },
                    { path: route_path.notFound, element: loadWithSuspense(<Error404Page />) },
                  ],
                },

                /***** Sharing *****/
                { path: '/s/:token', element: loadWithSuspense(<SharingPage />) },

                /***** Stage *****/
                {
                  path: route_path.main,
                  element: <StageLayout />,
                  children: [
                    {
                      path: route_path.uploadDraft,
                      element: loadWithSuspense(<UploadDraftPage />),
                    },
                    {
                      path: `${route_path.setupStyle}:projectId/`,
                      element: loadWithSuspense(<SetupStylePage />),
                    },
                    {
                      path: `${route_path.project}:projectId/`,
                      element: loadWithSuspense(<ProjectIdPage />),
                    },
                  ],
                },

                /***** Stage Workspace *****/
                {
                  path: route_path.main,
                  element: <StageLayout isWorkspaceVariant />,
                  children: [
                    {
                      path: route_path.workspaceUploadDraft,
                      element: loadWithSuspense(<UploadDraftPageNew isWorkspaceVariant />),
                    },
                    {
                      path: `${route_path.workspaceSetupStyle}:workspaceId/`,
                      element: loadWithSuspense(<SetupStylePage isWorkspaceVariant />),
                    },
                    {
                      path: `${route_path.workspace}:workspaceId/`,
                      element: loadWithSuspense(<FinalizeStagePage />),
                    },
                    {
                      path: route_path.textToSlides,
                      element: loadWithSuspense(<TextToSlidesPage />),
                    },
                    {
                      path: `${route_path.textToSlidesPreview}:workspaceId/`,
                      element: loadWithSuspense(<TextToSlidesPreviewPage />),
                    },
                  ],
                },

                /***** Wondercheck *****/
                { path: '/wondercheck/*', element: loadWithSuspense(<Wondercheck />) },

                /***** Legal *****/
                {
                  path: route_path.legal,
                  element: <DefaultLayout />,
                  children: [
                    { index: true, element: loadWithSuspense(<LegalPage />) },
                    { path: route_path.legal_terms, element: loadWithSuspense(<TermsPage />) },
                    { path: route_path.legal_cookie, element: loadWithSuspense(<CookiePage />) },
                    { path: route_path.legal_privacy, element: loadWithSuspense(<PrivacyPage />) },
                  ],
                },
              ],
            },

            /***** NotFound *****/
            { path: '*', element: loadWithSuspense(<Error404Page />) },
          ],
        },
      ],
    },
  ]);
